import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Languages } from "lucide-react";
import { CONTACT, whatsappUrl } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t, lang, toggle } = useLang();

  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/products", label: t("nav_products") },
    { to: "/about", label: t("nav_about") },
    { to: "/contact", label: t("nav_contact") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const waLink = whatsappUrl(CONTACT.primaryIntl, "Hello Mulywan! I'd like to know more about your products.");

  const LangBtn = ({ testid, className = "" }) => (
    <button
      onClick={toggle}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3.5 py-2 text-xs uppercase tracking-[0.15em] text-white/80 hover:text-gold hover:border-gold transition-colors ${className}`}
      aria-label="Toggle language"
    >
      <Languages size={14} />
      {lang === "en" ? "हिं" : "EN"}
    </button>
  );

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background,border,padding] duration-500 ${
          scrolled ? "glass-nav border-b border-gold/15 py-3" : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto max-w-7xl px-5 md:px-10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <img
              src="/assets/logo.png"
              alt="Mulywan"
              className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/40 group-hover:ring-gold transition-[box-shadow,transform] duration-500 group-hover:scale-105"
            />
            <div className="leading-none">
              <span className="block font-serif text-2xl text-gold-gradient tracking-wide">Mulywan</span>
              <span className="block text-[9px] tracking-[0.32em] text-white/50 uppercase">Cute Global Trade</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className="relative text-sm uppercase tracking-[0.2em] text-white/80 hover:text-gold transition-colors duration-300 py-1"
              >
                {l.label}
                {pathname === l.to && (
                  <motion.span layoutId="nav-underline" className="absolute -bottom-0.5 left-0 right-0 h-px bg-gold" />
                )}
              </Link>
            ))}
            <LangBtn testid="lang-toggle" />
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="navbar-whatsapp-btn"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold text-navy-900 hover:bg-gold-light transition-colors duration-300"
            >
              <MessageCircle size={15} /> {t("enquire")}
            </a>
          </nav>

          <div className="flex items-center gap-3 md:hidden">
            <LangBtn testid="lang-toggle-mobile" />
            <button className="text-gold p-1" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="navbar-menu-open">
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] md:hidden bg-navy-950/98 backdrop-blur-xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-gold/15">
              <span className="font-serif text-2xl text-gold-gradient">Mulywan</span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="navbar-menu-close" className="text-gold">
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-8">
              {links.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 * i + 0.1 }}>
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className="block font-serif text-5xl text-white/90 hover:text-gold transition-colors py-3"
                  >
                    <span className="text-gold/40 text-lg font-sans mr-3 align-middle">0{i + 1}</span>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="m-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm uppercase tracking-[0.2em] font-semibold text-navy-900"
              data-testid="mobile-whatsapp-btn"
            >
              <MessageCircle size={17} /> {t("whatsapp_us")}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
